import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useTheme, type ThemeChoice } from '../lib/theme';
import { roleLabel } from '../lib/format';
import type { Privilege } from '../lib/types';
import { Avatar, Button, Icon } from './ui';
import './AppShell.css';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /** Hidden entirely when the caller lacks this privilege. */
  needs?: Privilege;
  badge?: number;
}

interface NavGroup {
  heading: string;
  items: NavItem[];
}

/**
 * The three theme choices.
 *
 * "System" is the default and has to stay reachable: the old menu offered a
 * single toggle, so choosing light or dark once left no way back to following
 * the device — which is the setting most people actually want, since it tracks
 * their evening.
 */
const THEMES: Array<{
  value: ThemeChoice;
  label: string;
  hint: string;
  icon: (p?: { size?: number }) => ReactNode;
}> = [
  {
    value: 'system',
    label: 'System',
    hint: 'Follow the appearance setting on this device',
    icon: Icon.display,
  },
  { value: 'light', label: 'Light', hint: 'Always light', icon: Icon.sun },
  { value: 'dark', label: 'Dark', hint: 'Always dark', icon: Icon.moon },
];

export function AppShell() {
  const { user, signOut, can } = useAuth();
  const { choice, resolved, setChoice } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  if (!user) return null;

  const groups: NavGroup[] = [
    {
      heading: 'Portfolio',
      items: [
        { to: '/', label: 'Dashboard', icon: Icon.dashboard() },
        {
          to: '/applications',
          label: 'Applications',
          icon: Icon.applications(),
          needs: 'application.write',
        },
        {
          to: '/underwriting',
          label: 'Underwriting',
          icon: Icon.underwriting(),
          needs: 'application.decide',
        },
        { to: '/merchants', label: 'Merchants', icon: Icon.merchants() },
      ],
    },
    {
      heading: 'Ordering',
      items: [
        {
          to: '/equipment',
          label: 'Equipment',
          icon: Icon.equipment(),
          needs: 'equipment.order',
        },
        {
          to: '/supplies',
          label: 'Paper & supplies',
          icon: Icon.supplies(),
          needs: 'supplies.order',
        },
      ],
    },
    {
      heading: 'Insight',
      items: [
        { to: '/reports', label: 'Reports', icon: Icon.reports(), needs: 'residual.view' },
      ],
    },
    {
      heading: 'Administration',
      items: [
        { to: '/organization', label: 'Organization', icon: Icon.org() },
        {
          to: '/people',
          label: 'People',
          icon: Icon.merchants(),
          needs: 'org.manage_users',
        },
        {
          to: '/permissions',
          label: 'Roles & access',
          icon: Icon.permissions(),
          needs: 'org.invite',
        },
        {
          to: '/settings',
          label: 'Settings',
          icon: Icon.settings(),
          needs: 'org.move_node',
        },
        {
          to: '/recycle-bin',
          label: 'Recycle bin',
          icon: Icon.trash(),
          needs: 'org.manage_users',
        },
      ],
    },
  ];

  // An underwriter has no ordering or residual entries at all, so a group can
  // end up empty — drop it rather than render a bare heading.
  const visible = groups
    .map((g) => ({ ...g, items: g.items.filter((i) => !i.needs || can(i.needs)) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sidebar__brand">
          <span className="sidebar__mark">{Icon.logo()}</span>
          <span className="sidebar__name">Fairmount</span>
        </div>

        <nav className="sidebar__nav" aria-label="Main">
          {visible.map((group) => (
            <div className="navgroup" key={group.heading}>
              <div className="navgroup__heading">{group.heading}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) => `navlink ${isActive ? 'is-active' : ''}`}
                >
                  <span className="navlink__icon">{item.icon}</span>
                  <span className="navlink__label">{item.label}</span>
                  {item.badge ? <span className="navlink__badge">{item.badge}</span> : null}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar__user">
          <Avatar initials={user.initials} size={32} />
          <span className="sidebar__user-text">
            <span className="sidebar__user-name">{user.name}</span>
            <span className="sidebar__user-role">
              {user.level} · {roleLabel(user.role)}
            </span>
          </span>
          <button
            type="button"
            className="iconbtn"
            aria-label="Account menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {Icon.chevronDown({ size: 14 })}
          </button>

          {menuOpen && (
            <div className="usermenu" role="menu">
              <div className="usermenu__email">{user.email}</div>
              <button
                type="button"
                className="usermenu__item"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/security');
                }}
              >
                {Icon.permissions({ size: 14 })}
                Security
              </button>

              <div className="usermenu__section">Appearance</div>
              <div className="themepick" role="radiogroup" aria-label="Theme">
                {THEMES.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={choice === option.value}
                    className={`themepick__btn ${
                      choice === option.value ? 'is-active' : ''
                    }`}
                    // The menu deliberately stays open: picking a theme is a
                    // thing you look at, and closing the menu to see the result
                    // means reopening it to change your mind.
                    onClick={() => setChoice(option.value)}
                    title={option.hint}
                  >
                    {option.icon({ size: 14 })}
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
              {choice === 'system' && (
                <div className="usermenu__note">
                  Following your device, currently {resolved}.
                </div>
              )}
              <button
                type="button"
                className="usermenu__item"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  void signOut().then(() => navigate('/login'));
                }}
              >
                {Icon.logout({ size: 14 })}
                Sign out
              </button>
            </div>
          )}
        </div>
      </aside>

      <div className="main">
        <Outlet />
      </div>
    </div>
  );
}

/** Page header: breadcrumb, title and the actions for that screen. */
export function PageHeader({
  breadcrumb,
  title,
  actions,
  search,
}: {
  breadcrumb: string[];
  title: string;
  actions?: ReactNode;
  search?: boolean;
}) {
  return (
    <header className="pagehead">
      <div className="pagehead__titles">
        <div className="pagehead__crumb">
          {breadcrumb.map((c, i) => (
            <span key={c}>
              {i > 0 && <span className="pagehead__sep">/</span>}
              {c}
            </span>
          ))}
        </div>
        <h1 className="pagehead__title">{title}</h1>
      </div>

      {search && (
        <label className="searchbox">
          <span className="visually-hidden">Search merchants, MIDs, agents</span>
          <span className="searchbox__icon">{Icon.search({ size: 15 })}</span>
          <input type="search" placeholder="Search merchants, MIDs, agents" />
          <kbd className="searchbox__kbd">⌘K</kbd>
        </label>
      )}

      <div className="pagehead__actions">{actions}</div>
    </header>
  );
}

/**
 * The scope strip. Every screen carries one: it states which slice of the
 * tree the figures below cover, which is the single most load-bearing fact
 * on the page.
 */
export function ScopeBar({
  children,
  action,
}: {
  children: ReactNode;
  action?: ReactNode;
}) {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <div className="scopebar">
      <span className="scopebar__level">Level {user.level.slice(1)}</span>
      <span className="scopebar__text">{children}</span>
      <span className="spacer" />
      {action}
    </div>
  );
}

export function PageBody({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return <main className={`pagebody ${className}`}>{children}</main>;
}

export { Button };
